/**
 * ScrollToTop Component
 * Automatically scrolls to top of page when navigating forward
 * Preserves scroll position when navigating back (browser back button)
 */
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { isHomeSectionHash } from '@utils/scroll.utils';

// Store scroll positions for each pathname (including search params for uniqueness)
const scrollPositions = new Map<string, number>();

/**
 * ScrollToTop component that scrolls window to top on forward navigation
 * but preserves scroll position on back navigation
 * Should be placed inside BrowserRouter but outside Routes
 */
export const ScrollToTop: React.FC = () => {
  const { pathname, search, hash } = useLocation();
  const isBackNavigation = useRef(false);
  const previousPathname = useRef<string>(pathname);
  const previousSearch = useRef<string>(search);
  const isInitialMount = useRef(true);

  // Create a unique key for the current route (pathname + search)
  const getRouteKey = (path: string, searchParam: string) => `${path}${searchParam}`;

  useEffect(() => {
    // Listen for popstate events (back/forward button and navigate(-1))
    const handlePopState = () => {
      isBackNavigation.current = true;
    };

    // Also listen for beforeunload to store scroll position before navigation
    const handleBeforeUnload = () => {
      const currentKey = getRouteKey(pathname, search);
      scrollPositions.set(currentKey, window.scrollY);
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [pathname, search]);

  // Store scroll position on scroll (debounced would be better, but this works)
  useEffect(() => {
    const handleScroll = () => {
      const currentKey = getRouteKey(pathname, search);
      scrollPositions.set(currentKey, window.scrollY);
    };

    // Throttle scroll events to avoid performance issues
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', throttledScroll);
    };
  }, [pathname, search]);

  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousPathname.current = pathname;
      previousSearch.current = search;
      return;
    }

    const currentKey = getRouteKey(pathname, search);
    const previousKey = getRouteKey(previousPathname.current, previousSearch.current);

    // Store scroll position of previous route before navigation (if not back navigation)
    if (!isBackNavigation.current && previousKey !== currentKey) {
      scrollPositions.set(previousKey, window.scrollY);
    }

    // Check if we have a stored scroll position for this route (back navigation)
    const storedPosition = scrollPositions.get(currentKey);

    if (isBackNavigation.current && storedPosition !== undefined) {
      // Restore scroll position for back navigation
      // Use requestAnimationFrame to ensure DOM is ready and layout is complete
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo({
            top: storedPosition,
            left: 0,
            behavior: 'instant',
          });
        });
      });
      // Clear the flag after restoring
      isBackNavigation.current = false;
    } else if (!isBackNavigation.current) {
      if (!isHomeSectionHash(hash)) {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'instant',
        });
      }
    }

    // Update previous pathname and search
    previousPathname.current = pathname;
    previousSearch.current = search;
  }, [pathname, search, hash]);

  return null;
};

export default ScrollToTop;

